import type {AiChatSource, ChatMode, ChatOnLoadParams, ChatType, ProfileContext} from '@Components/ai-chat-window/ai-chat-window.types';
import type {MakeTextEditableLimit} from '@Components/ai-chat-window/lib/make-text-editable.library';
import type {AiChatBannerData} from '@AiChat/components/ai-chat-banner/ai-chat-banner.types';
import type {AiAgentMode} from '@AiChat/providers/ai-chat-provider.types';
import type {RecommendedTemplate} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';

export const AI_CHAT_HOME_SCROLL_ID = 'ai-chat-home-scroll-container';
export const AI_CHAT_PROMPT_BOX_ID = 'ai-chat';
export const AI_CHAT_QUICK_TIPS_URL_SEGMENT = 'quickTips';
export const AI_CHAT_ROOT_URL_SEGMENT = 'chat';
export const AI_CHAT_ASSETS_URL_SEGMENT = 'assets';
export const AI_CHAT_AGENT_URL_SEGMENT = 'agent';
export const AI_CHAT_MEMORY_URL_SEGMENT = 'memory';

export interface HistoryItem {
  idAiChat: string;
  title?: string;
  assetHashedFilename?: string;
  type: ChatType;
  mode: ChatMode;
  updatedAt: number;
  source?: AiChatSource | null;
}

export enum AiChatGeneratedAssetType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export enum AiChatGeneratedAssetFilter {
  ALL = 'all',
  IMAGES = 'images',
  VIDEOS = 'videos',
}

export interface AiChatGeneratedAsset {
  type: AiChatGeneratedAssetType;
  assetHashedFilename: string;
  idAiChat: string;
  width: number | null;
  height: number | null;
  duration: number | null;
  hasAudio: boolean | null;
  startTime: number | null;
  endTime: number | null;
  createdOn: string;
  messageId: string | null;
}

/** One consolidated fact from the memory library. The endpoint also returns ranking fields, which only a recall populates and this page has no use for. */
export interface UserMemoryFact {
  id: string;
  fact: string;
  updateTime: string | null;
}

/** One named subject the user taught us pixels for — the second memory tier, shown beside the facts. */
export interface UserMemoryVisualEntity {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  updateTime: string;
}

export interface UserMemories {
  facts: UserMemoryFact[];
  visualEntities: UserMemoryVisualEntity[];
}

export const EMPTY_USER_MEMORIES: UserMemories = {
  facts: [],
  visualEntities: [],
};

/** FAILED is distinct from an empty LOADED: "nothing stored yet" after an unreadable corpus is a claim we cannot make. */
export enum MemoryLoadState {
  LOADING,
  LOADED,
  FAILED,
}

export interface RecommendedTemplatesData {
  templates: RecommendedTemplate[];
  templateSearchTerm: string | null;
}

export interface AiChatConversationInput {
  chatOnLoadParams: ChatOnLoadParams;
  contextProfile?: ProfileContext;
}

export interface AiChatProps extends AiChatConversationInput {
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
  hasGeneratedAssets: boolean;
  isMemoryEnabled: boolean;
  shouldShowAssetsIntroSubtitle?: boolean;
  isAiChatCreateAVideoFeatureEnabled: boolean;
  isTemplateRedesignFeatureEnabled: boolean;
  promptBoxPlaceholderKey: string | null;
  canUseEditableTextLayers: boolean;
  chargeForMakeTextEditable: boolean;
  makeTextEditableLimit: MakeTextEditableLimit | null;
  banner?: AiChatBannerData;
  mode?: AiAgentMode;
}
